import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class PlatformService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowserEnv: boolean = isPlatformBrowser(this.platformId);

  isBrowser(): boolean {
    return this.isBrowserEnv;
  }

  /**
   * Leaves the SPA for an external URL — a payment gateway's hosted page.
   *
   * `assign` rather than `replace`: the gateway sends the learner back to a
   * return URL of our choosing, and keeping this entry in history means the
   * browser Back button does not skip straight past the checkout.
   *
   * No-ops off the browser, so callers do not need their own guard.
   */
  navigateExternal(url: string): void {
    if (!this.isBrowserEnv) return;
    window.location.assign(url);
  }

  /** Absolute origin, for building a return URL the gateway can redirect to. */
  origin(): string {
    if (!this.isBrowserEnv) return '';
    return window.location.origin;
  }

  getOS(): string {
    if (!this.isBrowserEnv) return 'Server';

    const ua = navigator.userAgent;

    if (/windows phone/i.test(ua)) return 'Windows Phone';
    if (/win/i.test(ua)) return 'Windows';
    if (/android/i.test(ua)) return 'Android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'iOS';
    if (/mac/i.test(ua)) return 'macOS';
    if (/linux/i.test(ua)) return 'Linux';

    return 'Unknown';
  }

  getOSVersion(): string {
    if (!this.isBrowserEnv) return 'Server';

    const ua = navigator.userAgent;
    let match;

    switch (this.getOS()) {
      case 'Windows':
        match = ua.match(/Windows NT ([\d.]+)/);
        return match?.[1] ?? 'Unknown';
      case 'Android':
        match = ua.match(/Android ([\d.]+)/);
        return match?.[1] ?? 'Unknown';
      case 'iOS':
        match = ua.match(/OS (\d+[_\d]*) like Mac OS X/);
        return match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
      case 'macOS':
        match = ua.match(/Mac OS X (\d+[_\d]*)/);
        return match?.[1]?.replace(/_/g, '.') ?? 'Unknown';
      default:
        return 'Unknown';
    }
  }

  getBrowser(): string {
    if (!this.isBrowserEnv) return 'Server';

    const ua = navigator.userAgent;

    if (/Edg/i.test(ua)) return 'Edge';
    if (/OPR|Opera/i.test(ua)) return 'Opera';
    if (/Chrome/i.test(ua)) return 'Chrome';
    if (/Safari/i.test(ua)) return 'Safari';
    if (/Firefox/i.test(ua)) return 'Firefox';
    if (/MSIE|Trident/i.test(ua)) return 'Internet Explorer';

    return 'Unknown';
  }

  getDeviceType(): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
    if (!this.isBrowserEnv) return 'unknown';

    const ua = navigator.userAgent;

    if (/Tablet|iPad/i.test(ua)) return 'tablet';
    if (/Mobi|Android/i.test(ua)) return 'mobile';

    return 'desktop';
  }

  getColorScheme(): 'light' | 'dark' | 'unknown' {
    if (!this.isBrowserEnv || !window.matchMedia) return 'unknown';

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';

    return 'unknown';
  }

  hasTouchSupport(): boolean {
    if (!this.isBrowserEnv) return false;

    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      ((navigator as { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0
    );
  }
}
